/**
 * CreateProjectWizard.tsx — окно «Новый проект», три шага: «О приложении» →
 * «Анализ» → «Подтверждение» (WEB-307 Context C5 FE; WEB-3760 юнит 2б — на ките
 * по эталону рескина `ProjWizard`, `design/cabinet-rescin/sections/org.jsx`
 * стр. 80–128, решение владельца 23.10 `proj-wizard-2310`: эталон — и вид, и
 * поведение; данные, права, деньги и защита — по коду).
 *
 * WEB-617: the project is created ONLY on the final «Создать проект» click —
 * not on a step transition. Previously (WEB-377/WEB-596-class bug) POST
 * /projects ran eagerly on advance from step 0; cancelling afterwards left an
 * orphan project in the DB that consumed a tier-limit slot. While the create
 * request is in flight the window cannot be closed: «Назад» is disabled and the
 * kit window gets `busy` (no Esc, no click outside).
 *
 * Шаг «Анализ» — бесплатный предпросмотр по ссылке (`analyzePreview`, без
 * списаний). Настоящий ПЛАТНЫЙ разбор (`contextApi.analyze`) требует id
 * проекта и идёт фоном ПОСЛЕ создания; его ход показывает «Главная» нового
 * проекта и вкладка «Контекст» (`backgroundAnalysis`). Решение владельца 28.10
 * («рек»): кредитов меньше цены разбора — разбор не запускается (отметка «не
 * хватило кредитов»); предпросмотр не удался — разбор не запускается.
 */
import React, { useEffect, useState } from 'react';
import { Button, Check, Help, Icon, Modal, Notice } from '@/components/kit/ds';
import { projectsApi } from '@/api/projects';
import { bridgeApi } from '@/api/bridge';
import {
  NO_BRIDGE_CONNECTION_METHOD,
  publishBridgeConfig,
} from '@/utils/projectMoneyProfile';
import { contextApi } from '@/api/context';
import type { AnalyzePreviewResult } from '@/api/context';
import {
  markBackgroundAnalysisNoBalance,
  startBackgroundAnalysis,
} from './backgroundAnalysis';
import { extractApiError } from '@/api/errorStatus';
import { useAppSelector, useAppDispatch } from '@/hooks/hooks';
import { useTier } from '@/hooks/useTier';
import { useOrgTopUp } from '@/hooks/useOrgTopUp';
import { contextAnalysisCost } from '@/utils/contextAnalysisCost';
import { selectWalletAvailableCents } from '@/utils/walletAvailable';
import {
  asTierLimitRejection,
  createLimitCounters,
  reasonCodeForCreateLimit,
} from '@/utils/tierLimit';
import { setBillingPrompt } from '@/store/billingSlice';
import { useShellToast } from '@/components/layout/shellToast';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import type { Project, UserTheme } from '@/types';
import type { ProjectSourceType } from '@/types/context';
import { pickStrings, useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { settingsStrings } from '@/i18n/dictionaries/settings';
import { localizeError } from '@/utils/localizeError';
import '../../styles/cabinet-proj-wizard.css';

export interface CreateProjectWizardProps {
  open: boolean;
  onClose: (project?: Project) => void;
}

/**
 * Ответ на вопрос об источнике. Пустая строка — «ещё не ответили».
 *
 * WEB-2126 (решение владельца по макету offscreen §CreateProjectModal):
 * умолчания `'app-store'` больше нет. Умолчание здесь было ОТВЕТОМ, которого
 * человек не давал: он жал «Далее», и проект заводился «с приложением из App
 * Store» — со всеми мостовыми ветками в кабинете.
 */
type WizardSource = ProjectSourceType | '';

/**
 * Ответ на вопрос «Как вы принимаете оплату?» — `'ours'` (через нас) либо
 * `'own'` (сам, у себя). Пустая строка — «ещё не ответили».
 *
 * WEB-2126, решение владельца `2122-К3`: вопрос задаётся ОДИН РАЗ, при
 * создании проекта. Ответ едет в `ownPayments` того же создания.
 */
type MoneyMode = 'ours' | 'own' | '';

/** Поля первого шага, у которых бывает ошибка под полем. */
type FieldKey = 'name' | 'src' | 'url' | 'money';
type FieldErrors = Partial<Record<FieldKey, string>>;

/**
 * Что будет с платным разбором после создания (эталон стр. 98–99, `how`):
 *  - `run` — запускаем фоном, ровно один раз;
 *  - `nobal` — кредитов меньше цены: не запускаем, ставим отметку;
 *  - `fail` — предпросмотр не удался, человек продолжил без анализа: не запускаем.
 */
type AnalysisPlan = 'run' | 'nobal' | 'fail';

/** Источники. App Store и Google Play — имена собственные, не переводятся. */
function sourceOptions(s: {
  wizardSourceWebsite: string;
  wizardSourceNone: string;
}): Array<{ label: string; value: ProjectSourceType }> {
  return [
    { label: 'App Store', value: 'app-store' },
    { label: 'Google Play', value: 'google-play' },
    { label: s.wizardSourceWebsite, value: 'website' },
    { label: s.wizardSourceNone, value: 'none' },
  ];
}

/** Заглушка поля ссылки по источнику (эталон стр. 111). */
const URL_PLACEHOLDER: Record<string, string> = {
  'app-store': 'https://apps.apple.com/app/…',
  'google-play': 'https://play.google.com/store/apps/details?id=…',
  website: 'https://example.com',
};

/** Ссылка годится: http(s), хост с точкой (эталон стр. 93). */
const URL_OK = /^https?:\/\/\S+\.\S+/;

/**
 * Записать у нового проекта путь «приложение не подключаю» (WEB-2126).
 *
 * ⚠ Отказ НЕ роняет создание: проект уже заведён, и человека нельзя оставить
 * с ошибкой вместо готового проекта. Несохранённый путь безопасен — внутри
 * проекта его спросят вопросом первого входа, как у всех прежних проектов.
 */
async function saveNoBridgePath(projectId: string): Promise<void> {
  try {
    const res = await bridgeApi.updateConfig(projectId, {
      connectionMethod: NO_BRIDGE_CONNECTION_METHOD,
    });
    if (res?.data) {
      publishBridgeConfig(projectId, res.data);
    }
  } catch {
    // см. докблок: молчание здесь осознанное, запасной путь — вопрос
    // первого входа внутри проекта.
  }
}

/**
 * Фоновый ПЛАТНЫЙ разбор приложения по ссылке (WEB-1362 п.2): исход
 * запоминается в `backgroundAnalysis` — «Главная» и вкладка «Контекст»
 * на него подписаны.
 */
function runAnalyzeInBackground(projectId: string, url: string): void {
  startBackgroundAnalysis(projectId, () =>
    contextApi.analyze(projectId, { sourceUrl: url, attempt: 1 }),
  );
}

// WEB-667: причина отказа предпросмотра словами. 422 / PREVIEW_UNPARSEABLE —
// «не разобрали страницу», прочее — «не удалось выполнить анализ» (WEB-3760:
// ветки PRO_REQUIRED нет — тариф один). WEB-1845: код — через extractApiError.
function previewErrorMessage(err: unknown): string {
  const { status, code } = extractApiError(err);
  const s = pickStrings(settingsStrings);
  if (status === 422 || code === 'PREVIEW_UNPARSEABLE') {
    return s.wizardPreviewUnparseable;
  }
  return s.wizardAnalysisFailed;
}

/**
 * «Пополнить» в плашке «мало кредитов» — только при праве пополнять
 * (`useOrgTopUp`). Хуку нужен роутер, поэтому он живёт в компоненте, который
 * рисуется только вместе с плашкой (как `EditorTopUpButton`, WEB-1246).
 */
function NoBalanceTopUp({
  orgId,
  label,
  onLeave,
}: {
  orgId: string | null;
  label: string;
  onLeave: () => void;
}): React.ReactElement | null {
  const { canTopUp, goToBalance } = useOrgTopUp(orgId);
  if (!canTopUp) return null;
  return (
    <Button
      size="sm"
      onClick={() => {
        onLeave();
        void goToBalance();
      }}
    >
      {label}
    </Button>
  );
}

export function CreateProjectWizard({
  open,
  onClose,
}: CreateProjectWizardProps) {
  const s = useStrings(settingsStrings);
  const c = useStrings(commonStrings);
  const showToast = useShellToast();
  // Мастер рисуется и вне корня `.ds` (OrgBootstrapGate ставит его рядом с
  // содержимым) — свой корень с темой человека; где корень уже есть, второй
  // не мешает (`.pw-host` — без своей коробки).
  const userTheme = useAppSelector(
    (state) =>
      (state as { auth?: { user?: { theme?: UserTheme } | null } }).auth?.user
        ?.theme,
  );
  const root = useCabinetRootAttributes(userTheme);
  // WEB-329: scope creation to the active org — BE resolveOrgId rejects (400) a
  // user with >1 org when no orgId is given. Send the active org explicitly.
  const activeOrgId = useAppSelector(
    (state) => state.organizations?.activeOrgId,
  );
  // WEB-1362 п.2: цена платного разбора — из каталога розницы, не литералом;
  // формула одна на продукт (utils/contextAnalysisCost). Каталог не загружен —
  // число не называем.
  const costCatalog = useAppSelector(
    (state) => state.billing?.costCatalog ?? null,
  );
  const analyzeCost = contextAnalysisCost(costCatalog);
  // WEB-3760: свободный остаток кошелька (центы = кредиты). Не загружен — null.
  const walletRaw = useAppSelector(selectWalletAvailableCents);
  const available = typeof walletRaw === 'number' ? walletRaw : null;
  const dispatch = useAppDispatch();
  // Пред-гейт лимита проектов: проектов в организации >= лимита — вместо
  // шагов предупреждение. canCreateProject при незагруженном billing = true.
  // BE-402 на create остаётся страховкой.
  const { canCreateProject, tier, limits, usage } = useTier();
  const gated = !canCreateProject;
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [sourceType, setSourceType] = useState<WizardSource>('');
  const [money, setMoney] = useState<MoneyMode>('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // WEB-667: результат бесплатного предпросмотра (категория) и причина отказа.
  const [preview, setPreview] = useState<AnalyzePreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Решение «мало кредитов» принимается на «Продолжить» и держится до
  // создания: остаток, догрузившийся на подтверждении, не запускает разбор
  // без предпросмотра.
  const [skippedNoBalance, setSkippedNoBalance] = useState(false);

  // Ссылка нужна только у источника-приложения или сайта. Пустой ответ — не «none».
  const needsUrl = sourceType !== 'none' && sourceType !== '';
  const noApp = sourceType === 'none';
  // Мало кредитов: остаток и цена известны, остаток строго меньше цены
  // (эталон стр. 85: have < AN_COST).
  const noBalance =
    needsUrl &&
    available !== null &&
    analyzeCost !== null &&
    available < analyzeCost;
  const sourceLabel =
    sourceOptions(s).find((opt) => opt.value === sourceType)?.label ?? '';
  const plan: AnalysisPlan = skippedNoBalance
    ? 'nobal'
    : previewError
      ? 'fail'
      : 'run';

  // Шаг «Анализ»: бесплатный предпросмотр, пока запрос в полёте — полоска.
  // Успех — сам переход на «Подтверждение»; отказ — причина и «Продолжить без
  // анализа». Уход с шага отменяет ответ.
  useEffect(() => {
    if (step !== 1) return undefined;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    contextApi
      .analyzePreview({
        sourceUrl: sourceUrl.trim(),
        orgId: activeOrgId ?? undefined,
      })
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        setStep(2);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(previewErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const resetState = () => {
    setStep(0);
    setName('');
    // WEB-2126: сброс возвращает ОБА вопроса в «не отвечено» — мастер при
    // закрытии не размонтируется, и ответ уехал бы в следующий проект.
    setSourceType('');
    setMoney('');
    setSourceUrl('');
    setErrors({});
    setIsLoading(false);
    setCreateError(null);
    setPreview(null);
    setPreviewError(null);
    setSkippedNoBalance(false);
  };

  const handleCancel = () => {
    // WEB-617: до «Создать проект» проекта нет — закрытие всегда отдаёт
    // undefined. Во время запроса закрыть нельзя (окно busy, «Назад» погашена).
    if (isLoading) return;
    resetState();
    onClose(undefined);
  };

  /** Закрыть мастер со сбросом без проекта — перед уходом на тариф или баланс. */
  const leave = () => {
    resetState();
    onClose();
  };

  // «Повысить тариф» из гейта — закрываем мастер и открываем глобальную
  // PricingModal (тот же путь, что и 402 на create). WEB-1368 п.2.1: со сбросом.
  const handleUpgrade = () => {
    leave();
    dispatch(
      setBillingPrompt({
        tier: 'PRO',
        action: 'createProject',
        ...createLimitCounters('project', tier, usage, limits),
        // Ревизия пачки 26 (Ф4): причина — из общей двери (WEB-1844).
        reasonCode: reasonCodeForCreateLimit('project', tier),
      }),
    );
  };

  /** Ввод в поле снимает только его ошибку (эталон стр. 84). */
  const clearError = (key: FieldKey) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleNextFromInfo = () => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = s.wizardNameRequired;
    if (!sourceType) next.src = s.wizardSourceRequired;
    if (needsUrl && !URL_OK.test(sourceUrl.trim()))
      next.url = s.wizardUrlRequired;
    if (!money) next.money = s.wizardMoneyRequired;
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setCreateError(null);
    setPreview(null);
    setPreviewError(null);
    setSkippedNoBalance(noBalance);
    // Без ссылки и при нехватке кредитов шаг «Анализ» пропускается (стр. 96).
    setStep(needsUrl && !noBalance ? 1 : 2);
  };

  const handleFinish = async () => {
    if (isLoading) return; // WEB-617/WEB-596-class: double-submit guard.
    setIsLoading(true);
    setCreateError(null);
    try {
      const createPayload: Record<string, unknown> = {
        name: name.trim(),
        // WEB-2126: ответ про оплату — частью ТОГО ЖЕ создания, явным false тоже.
        ownPayments: money === 'own',
      };
      if (activeOrgId) {
        createPayload.orgId = activeOrgId;
      }
      if (needsUrl && sourceUrl.trim()) {
        createPayload.sourceUrl = sourceUrl.trim();
      }
      const projectRes = await projectsApi.create(createPayload);
      const newProject = projectRes.data as Project;

      if (needsUrl && sourceUrl.trim()) {
        if (plan === 'run') {
          runAnalyzeInBackground(newProject.id, sourceUrl.trim());
        } else if (plan === 'nobal') {
          markBackgroundAnalysisNoBalance(newProject.id);
        }
      }

      if (noApp) {
        await saveNoBridgePath(newProject.id);
      }

      showToast(s.wizardCreated, 'ok');
      resetState();
      onClose(newProject);
    } catch (err) {
      // WEB-1889 п.2: тарифность судит ОБЩИЙ распознаватель asTierLimitRejection;
      // нетарифный 402 (задолженность, пустой кошелёк) — строкой в окне.
      const tierLimit = asTierLimitRejection(err);
      if (tierLimit) {
        // Unified 402 upsell: закрыть мастер (WEB-1368 п.2.1 — со сбросом) и
        // отдать слово глобальной PricingModal.
        leave();
        dispatch(
          setBillingPrompt({
            tier: 'PRO',
            action: 'createProject',
            usage: tierLimit.usage,
            limit: tierLimit.limit,
            // WEB-1908: причина отказа — окно тарифа скажет, что упёрлось.
            reasonCode: tierLimit.reasonCode,
          }),
        );
      } else {
        // WEB-1955: разобранный отказ уходит в реестр кодов ЦЕЛИКОМ.
        setCreateError(localizeError(err, s.wizardCreateFailed));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const summarySource = (): string => {
    if (!needsUrl) return s.wizardSummaryNoApp;
    if (plan === 'nobal') return s.wizardSummaryNoBalance(sourceLabel);
    if (plan === 'fail') return s.wizardSummaryFailed(sourceLabel);
    const category = preview?.category
      ? s.wizardCategorySuffix(preview.category)
      : '';
    return s.wizardSummaryWillFill(sourceLabel + category);
  };

  const renderInfo = () => (
    <>
      <label className="lbl">
        <span>{s.wizardNameLabel}</span>
        <input
          className={'input' + (errors.name ? ' bad' : '')}
          autoComplete="off"
          data-testid="proj-name"
          placeholder={s.projectNamePlaceholder}
          maxLength={64}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearError('name');
          }}
          autoFocus
        />
        {errors.name && <span className="hint bad">{errors.name}</span>}
      </label>
      <div className="lbl">
        <span>{s.wizardSourceLabel}</span>
        <div className="pw-opts" data-testid="proj-src">
          {sourceOptions(s).map((opt) => (
            <Check
              key={opt.value}
              round
              name="pw-src"
              label={opt.label}
              checked={sourceType === opt.value}
              onChange={() => {
                setSourceType(opt.value);
                clearError('src');
              }}
            />
          ))}
        </div>
        {errors.src && <span className="hint bad">{errors.src}</span>}
      </div>
      {needsUrl && (
        <label className="lbl">
          <span>
            {s.wizardUrlLabel}
            <Help tip={s.wizardUrlHint} />
          </span>
          <input
            className={'input' + (errors.url ? ' bad' : '')}
            autoComplete="off"
            data-testid="proj-url"
            placeholder={URL_PLACEHOLDER[sourceType] ?? 'https://…'}
            value={sourceUrl}
            onChange={(e) => {
              setSourceUrl(e.target.value);
              clearError('url');
            }}
          />
          {/* WEB-1362 п.2 (владелец 02.09): короткая подпись о платности, без
              галочки согласия и без отдельного окна. */}
          {errors.url ? (
            <span className="hint bad">{errors.url}</span>
          ) : (
            !noBalance && (
              <span className="hint" data-testid="pw-cost">
                {analyzeCost
                  ? s.wizardAnalysisPaidWithCost(analyzeCost)
                  : s.wizardAnalysisPaid}
              </span>
            )
          )}
        </label>
      )}
      {noBalance && available !== null && analyzeCost !== null && (
        <Notice
          kind="warn"
          title={s.wizardNoBalanceTitle(analyzeCost, available)}
          action={
            <NoBalanceTopUp
              orgId={activeOrgId ?? null}
              label={s.wizardTopUp}
              onLeave={leave}
            />
          }
        >
          {s.wizardNoBalanceText}
        </Notice>
      )}
      <div className="lbl">
        <span>{s.wizardMoneyLabel}</span>
        <div className="pw-opts" data-testid="proj-money">
          <Check
            round
            name="pw-money"
            label={s.wizardMoneyOurs}
            checked={money === 'ours'}
            onChange={() => {
              setMoney('ours');
              clearError('money');
            }}
          />
          <Check
            round
            name="pw-money"
            label={s.wizardMoneyOwn}
            checked={money === 'own'}
            onChange={() => {
              setMoney('own');
              clearError('money');
            }}
          />
        </div>
        {errors.money && <span className="hint bad">{errors.money}</span>}
      </div>
      {/* «Сам, у себя» — кабинет свернёт денежные разделы (эталон стр. 117, при
          любом источнике). «Через нас» + «не подключаю» (WEB-2234) — покупателя
          после оплаты нужно куда-то вести (стр. 118). */}
      {money === 'own' && (
        <Notice kind="info" title={s.wizardOwnAllTitle}>
          {s.wizardOwnAllText}
        </Notice>
      )}
      {money === 'ours' && noApp && (
        <Notice kind="warn" title={s.wizardPayUsNoAppTitle}>
          {s.wizardPayUsNoAppText}
        </Notice>
      )}
    </>
  );

  const renderAnalysis = () =>
    previewError ? (
      <Notice kind="error" title={previewError} />
    ) : (
      <div className="pw-an" data-testid="pw-analyzing">
        <div className="bar">
          <i />
        </div>
        <span>{s.wizardAnalyzing}</span>
      </div>
    );

  const renderConfirm = () => (
    <>
      <dl className="pw-sum" data-testid="pw-summary">
        <dt>{s.wizardSumName}</dt>
        <dd>{name.trim()}</dd>
        <dt>{s.wizardSumSource}</dt>
        <dd>{summarySource()}</dd>
        <dt>{s.wizardSumMoney}</dt>
        <dd>{money === 'own' ? s.wizardMoneyOwn : s.wizardMoneyOurs}</dd>
      </dl>
      {createError && <Notice kind="error" title={createError} />}
    </>
  );

  const steps = [s.wizardStepAbout, s.wizardStepAnalysis, s.wizardStepConfirm];

  // Подвал по шагам (эталон стр. 100–102).
  const footer = gated ? undefined : step === 0 ? (
    <>
      <Button kind="ghost" onClick={handleCancel}>
        {c.cancel}
      </Button>
      <Button kind="primary" onClick={handleNextFromInfo} data-testid="pw-next">
        {s.wizardContinue}
      </Button>
    </>
  ) : step === 1 ? (
    <>
      <Button kind="ghost" onClick={() => setStep(0)}>
        {c.back}
      </Button>
      {previewError && (
        <Button kind="primary" onClick={() => setStep(2)} data-testid="pw-noan">
          {s.wizardContinueNoAnalysis}
        </Button>
      )}
    </>
  ) : (
    <>
      <Button kind="ghost" onClick={() => setStep(0)} disabled={isLoading}>
        {c.back}
      </Button>
      <Button
        kind="primary"
        onClick={() => void handleFinish()}
        loading={isLoading}
        data-testid="proj-new-ok"
      >
        {s.wizardCreate}
      </Button>
    </>
  );

  if (!open) return null;

  return (
    <div {...root} className={`${root.className} pw-host`}>
      <Modal
        testid="proj-new"
        width={560}
        open={open}
        onClose={handleCancel}
        title={s.wizardTitle}
        dismiss
        busy={isLoading}
        footer={footer}
      >
        <div className="pw-body">
          {gated ? (
            /* Предел проектов — ВМЕСТО шагов. Состояние без эталона: вид — Notice
               кита; тариф один, название тарифа не пишется (WEB-3760). */
            <Notice
              kind="warn"
              testid="wizard-tier-gate"
              title={s.wizardOrgLimit(
                s.projectsCount(limits?.maxProjects ?? 0),
              )}
              action={
                <Button
                  size="sm"
                  kind="primary"
                  onClick={handleUpgrade}
                  data-testid="wizard-tier-gate-upgrade"
                >
                  {s.wizardUpgrade}
                </Button>
              }
            >
              {s.wizardLimitText}
            </Notice>
          ) : (
            <>
              <ol className="pw-steps" aria-label={s.wizardTitle}>
                {steps.map((title, i) => (
                  <li
                    key={title}
                    className={i === step ? 'on' : i < step ? 'done' : ''}
                    aria-current={i === step ? 'step' : undefined}
                  >
                    <span className="n">
                      {i < step ? <Icon name="check" size={12} /> : i + 1}
                    </span>
                    {title}
                  </li>
                ))}
              </ol>
              {step === 0 && renderInfo()}
              {step === 1 && renderAnalysis()}
              {step === 2 && renderConfirm()}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
